import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Input,
  Text,
  useToast,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  Select,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import axios from "axios";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import Navbar from "../../../Components/Navbar";
import * as XLSX from "xlsx";
import { RiDeleteBin2Line } from "react-icons/ri";
import { useSelector } from "react-redux";
import CompanySidebar from "../../../Components/CompanySidebar";
import { format, formatDistanceToNow } from "date-fns";

const CompanySheets = () => {
  const companyId = useSelector((store) => store.authentication.users.id);
  const [files, setFiles] = useState([]);
  const [sheetsFromServer, setSheetsFromServer] = useState([]);
  const [newSheetName, setNewSheetName] = useState("");
  //console.log("sheetsFromServer", sheetsFromServer);
  //console.log("files", files);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [selectedTab, setSelectedTab] = useState(0);
  const [editingSheetIndex, setEditingSheetIndex] = useState(0);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => {
    fetchSheets();
  }, []);

  const fetchSheets = async () => {
    try {
      const res = await axios.get(
        `https://develop-project-360.onrender.com/excelfilesComp/${companyId}`
      );
      setSheetsFromServer(res.data);
    } catch (err) {
      console.error("Failed to fetch sheets", err);
    }
  };

  const handleDeleteSheet = async (fileId) => {
    try {
      await axios.delete(
        `https://develop-project-360.onrender.com/excelfiles/${fileId}`
      );
      toast({ title: "Deleted", status: "info" });
      fetchSheets();
    } catch (err) {
      toast({ title: "Error deleting", status: "error" });
    }
  };

  const addFileToState = (name, fileId, rows = [[""]]) => {
    setFiles((prev) => [
      ...prev,
      {
        name,
        fileId,
        rows: Array.isArray(rows) ? rows : [[""]],
        formats: [{}],
      },
    ]);
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const sanitizedRows = rawRows
          .filter((row) => row.length > 0)
          .map((row) =>
            row.map((cell) => ({
              value: cell || "",
              fontWeight: "normal",
              fontStyle: "normal",
              textDecoration: "none",
              color: "#000000",
              backgroundColor: "#ffffff",
            }))
          );
        resolve(sanitizedRows);
      };

      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  };

  const normalizeRows = (rows) =>
    rows.map((row) =>
      Array.isArray(row)
        ? row.map((cell) => {
            if (typeof cell === "object" && cell !== null) return cell;
            return {
              value: cell !== undefined && cell !== null ? cell : "",
              fontWeight: "normal",
              fontStyle: "normal",
              textDecoration: "none",
              color: "#000000",
              backgroundColor: "#ffffff",
            };
          })
        : []
    );

  const handleEditSheet = async (sheet) => {
    try {
      const res = await axios.get(
        `https://develop-project-360.onrender.com/getFileData/${sheet._id}`
      );
      let rows = res.data?.rows || [];

      if (!rows || rows.length === 0) {
        toast({
          title: "No sheet data found",
          description: "Using local fallback data if available.",
          status: "warning",
          isClosable: true,
        });
        const localSheet = files.find((f) => f.fileId === sheet._id);
        rows = localSheet?.rows || [[""]];
      }

      setFiles([{ ...sheet, rows: normalizeRows(rows) }]);
      setEditingSheetIndex(0);
      setSelectedTab(1);
    } catch (err) {
      console.error("Error loading sheet data:", err);
      toast({ title: "Error loading sheet", status: "error" });
    }
  };

  const handleEditSheet2 = async (sheet) => {
    try {
      const res = await axios.get(
        `https://develop-project-360.onrender.com/getFileData/${sheet._id}`
      );
      const { rows } = res.data;
      const normalized = normalizeRows(rows); // Fixed logic
      setFiles([{ ...sheet, rows: normalized }]);
      setEditingSheetIndex(0);
      setSelectedTab(1);
    } catch (err) {
      console.error("Error loading sheet data:", err);
      toast({ title: "Error loading sheet", status: "error" });
    }
  };

  const handleCreateSheet = async () => {
    if (!newSheetName) {
      toast({
        title: "Missing data",
        description: "Please provide a sheet name",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", newSheetName);
      formData.append("companyId", companyId);

      let rows = [];

      if (selectedFile) {
        rows = await readExcelFile(selectedFile);
        formData.append("file", selectedFile);
      } else {
        const workbook = new ExcelJS.Workbook();
        workbook.addWorksheet("Sheet1");
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const file = new File([blob], `${newSheetName}.xlsx`, {
          type: blob.type,
        });
        formData.append("file", file);
        rows = [[""]];
      }

      const response = await axios.post(
        "https://develop-project-360.onrender.com/excel/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // const { fileId } = response.data;

      // // ✅ Save parsed rows immediately
      // await addMultipleRows(fileId, rows);

      // // ✅ Add to local state for immediate editing
      // addFileToState(newSheetName || selectedFile.name, fileId, rows);

      const { fileId } = response.data;

      // ✅ Save parsed rows immediately
      await addMultipleRows(fileId, rows);

      // ✅ Add to local state and open it immediately
      const newSheet = {
        name: newSheetName || selectedFile.name,
        fileId,
        rows,
        formats: [{}],
      };

      setFiles((prev) => {
        const updated = [...prev, newSheet];
        setEditingSheetIndex(updated.length - 1); // ✅ Focus on this new sheet
        return updated;
      });

      toast({
        title: "Sheet created",
        description: "Sheet successfully created and stored.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setSelectedTab(1);
      setNewSheetName("");
      setSelectedFile(null);
      fetchSheets();
      onClose();
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed to create sheet.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateSheet2 = async () => {
    if (!newSheetName) {
      toast({
        title: "Missing data",
        description: "Please provide a sheet name",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", newSheetName);
      formData.append("companyId", companyId);

      let rows = [];

      if (selectedFile) {
        rows = await readExcelFile(selectedFile); // parsed here
        formData.append("file", selectedFile);
      } else {
        const workbook = new ExcelJS.Workbook();
        workbook.addWorksheet("Sheet1");
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const file = new File([blob], `${newSheetName}.xlsx`, {
          type: blob.type,
        });
        formData.append("file", file);
        rows = [[""]];
      }

      const response = await axios.post(
        "https://develop-project-360.onrender.com/excel/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const { fileId } = response.data;

      // Cache parsed rows locally so edit will work
      addFileToState(newSheetName || selectedFile.name, fileId, rows);

      toast({
        title: "Sheet created",
        description: "Sheet successfully created and stored.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setSelectedTab(1);
      setNewSheetName("");
      setSelectedFile(null);
      fetchSheets();
      onClose();
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed to create sheet.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const addMultipleRows = useCallback(
    async (fileId, rows) => {
      const toastId = "save-toast";
      // Skip if fileId is missing (new sheet not yet saved)
      if (!fileId) return;
      try {
        const { data } = await axios.post(
          "https://develop-project-360.onrender.com/addRows",
          { fileId, rows },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!toast.isActive(toastId)) {
          toast({
            id: toastId,
            title: "All changes saved",
            description: "Your updates were saved successfully.",
            status: "success",
            duration: 2000,
            isClosable: true,
            position: "top-right",
            variant: "subtle",
          });
        }

        return data;
      } catch (err) {
        const errorId = "save-error-toast";
        if (!toast.isActive(errorId)) {
          toast({
            id: errorId,
            title: "Save failed",
            description: err.response?.data?.message || err.message,
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
            variant: "solid",
          });
        }
        throw err;
      }
    },
    [toast]
  );

  // Save by editing index
  const saveEditedSheet = () => {
    const sheet = files[editingSheetIndex];
    if (!sheet || !sheet._id) return; // Prevent saving unsaved sheets
    addMultipleRows(sheet._id, sheet.rows);
  };

  const saveRows = useCallback(
    (fileIndex) => {
      const file = files[fileIndex];
      if (!file || !file.fileId) return; // Skip unsaved
      addMultipleRows(file.fileId, file.rows).catch(() => {});
    },
    [files, addMultipleRows]
  );

  // Unified cell change handler
  const handleCellChange = (fileIndex, rowIndex, colIndex, value) => {
    setFiles((prevFiles) => {
      const filesCopy = [...prevFiles];
      const file = { ...filesCopy[fileIndex] };
      file.rows = file.rows.map((row, rIdx) =>
        rIdx === rowIndex
          ? row.map((cell, cIdx) => (cIdx === colIndex ? value : cell))
          : row
      );
      filesCopy[fileIndex] = file;
      return filesCopy;
    });

    saveRows(fileIndex); // Try saving if possible
  };

  // Add a new row
  const addRow = (fileIndex) => {
    setFiles((prevFiles) => {
      const filesCopy = [...prevFiles];
      const file = { ...filesCopy[fileIndex] };
      const columnCount = file.rows[0]?.length || 1;
      const newRow = Array(columnCount).fill("");
      file.rows = [...file.rows, newRow];
      filesCopy[fileIndex] = file;
      return filesCopy;
    });

    saveRows(fileIndex);
  };

  // Add a new column
  const addColumn = (fileIndex) => {
    setFiles((prevFiles) => {
      const filesCopy = [...prevFiles];
      const file = { ...filesCopy[fileIndex] };
      file.rows = file.rows.map((row) => [...row, ""]);
      filesCopy[fileIndex] = file;
      return filesCopy;
    });

    saveRows(fileIndex);
  };

  // Remove a row
  const removeRow = (fileIndex, rowIndex) => {
    setFiles((prevFiles) => {
      const filesCopy = [...prevFiles];
      const file = { ...filesCopy[fileIndex] };

      if (file.rows.length === 1) {
        toast({
          title: "Cannot remove row",
          description: "At least one row must be present.",
          status: "warning",
          duration: 2000,
          isClosable: true,
        });
        return prevFiles;
      }

      file.rows = file.rows.filter((_, idx) => idx !== rowIndex);
      filesCopy[fileIndex] = file;
      return filesCopy;
    });

    saveRows(fileIndex);
  };

  const hexToARGB = (hex) => {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    return "FF" + hex.toUpperCase(); // ExcelJS expects ARGB
  };

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();

    files.forEach((file) => {
      const sheet = workbook.addWorksheet(file.name);

      file.rows.forEach((row, rowIndex) => {
        const rowRef = sheet.addRow(
          row.map((cell) => (typeof cell === "object" ? cell.value : cell))
        );

        row.forEach((cell, colIndex) => {
          const cellRef = rowRef.getCell(colIndex + 1);
          const formatted = typeof cell === "object" ? cell : { value: cell };

          // Font setup
          const font = {};
          if (formatted.fontWeight === "bold") font.bold = true;
          if (formatted.fontStyle === "italic") font.italic = true;
          if (formatted.textDecoration === "underline") font.underline = true;
          if (formatted.color)
            font.color = { argb: hexToARGB(formatted.color) };

          if (Object.keys(font).length > 0) cellRef.font = font;

          // Fill setup
          if (formatted.backgroundColor) {
            cellRef.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: hexToARGB(formatted.backgroundColor) },
            };
          }
        });
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, files[0]?.name || "Untitled.xlsx");

    toast({
      title: "Excel downloaded",
      description: "Your Excel file has been saved.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const exportExcel2 = async () => {
    const workbook = new ExcelJS.Workbook();

    files.forEach((file) => {
      const sheet = workbook.addWorksheet(file.name);

      file.rows.forEach((row, rowIndex) => {
        const valuesOnly = row.map((cell) =>
          typeof cell === "object" && cell !== null ? cell.value : cell
        );

        const rowRef = sheet.addRow(valuesOnly);

        row.forEach((cell, colIndex) => {
          const cellValue = typeof cell === "object" ? cell.value : cell;
          const cellRef = rowRef.getCell(colIndex + 1);

          // Optional: Apply formatting based on your cell object
          if (typeof cell === "object") {
            if (cell.fontWeight === "bold") {
              cellRef.font = cellRef.font || {};
              cellRef.font.bold = true;
            }
            if (cell.fontStyle === "italic") {
              cellRef.font = cellRef.font || {};
              cellRef.font.italic = true;
            }
            if (cell.textDecoration === "underline") {
              cellRef.font = cellRef.font || {};
              cellRef.font.underline = true;
            }
            if (cell.color) {
              cellRef.font = cellRef.font || {};
              cellRef.font.color = { argb: hexToARGB(cell.color) };
            }
            if (cell.backgroundColor) {
              cellRef.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: hexToARGB(cell.backgroundColor) },
              };
            }
          }

          if (rowIndex === 0) {
            cellRef.font = {
              ...cellRef.font,
              bold: true,
              italic: true,
            };
          }
        });
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      files.length > 0 && files[0].name ? files[0].name : "Untitled.xlsx"
    );

    toast({
      title: "Excel downloaded",
      description: "Your Excel file has been saved.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const getDefaultCell = (cell = {}) => ({
    value: cell.value || "",
    fontWeight: cell.fontWeight || "normal",
    fontStyle: cell.fontStyle || "normal",
    textDecoration: cell.textDecoration || "none",
    color: cell.color || "#000000",
    backgroundColor: cell.backgroundColor || "#ffffff",
  });

  // ✅ Utility to get default for a specific style property
  const getDefaultStyle = (key) => {
    const defaults = {
      fontWeight: "normal",
      fontStyle: "normal",
      textDecoration: "none",
      color: "#000000",
      backgroundColor: "#ffffff",
    };
    return defaults[key];
  };

  // ✅ Computes common styles among selected cells
  const activeStyles = React.useMemo(() => {
    if (!files || selectedCells.size === 0) return {};

    let commonStyles = {
      fontWeight: undefined,
      fontStyle: undefined,
      textDecoration: undefined,
      color: undefined,
      backgroundColor: undefined,
    };

    let first = true;

    selectedCells.forEach((cellKey) => {
      const [fileIndex, rowIndex, colIndex] = cellKey
        .split("-")
        .map((i) => parseInt(i, 10));

      const cell = files[fileIndex]?.rows?.[rowIndex]?.[colIndex];
      const current = typeof cell === "string" ? { value: cell } : cell || {};

      if (first) {
        // Use the first cell's styles as a baseline
        commonStyles = {
          fontWeight: current.fontWeight || "normal",
          fontStyle: current.fontStyle || "normal",
          textDecoration: current.textDecoration || "none",
          color: current.color || "#000000",
          backgroundColor: current.backgroundColor || "#ffffff",
        };
        first = false;
      } else {
        for (let key in commonStyles) {
          if (commonStyles[key] !== (current[key] || getDefaultStyle(key))) {
            commonStyles[key] = undefined; // If any mismatch, unset the style
          }
        }
      }
    });

    return commonStyles;
  }, [selectedCells, files]);

  // ✅ Extracts the currently active cell (used for formatting toggle buttons)
  const activeCell = React.useMemo(() => {
    if (!selectedCells || selectedCells.size === 0) return null;

    const firstKey = Array.from(selectedCells)[0];
    const [fileIndex, rowIndex, colIndex] = firstKey
      .split("-")
      .map((i) => parseInt(i, 10));

    return files?.[fileIndex]?.rows?.[rowIndex]?.[colIndex] || null;
  }, [selectedCells, files]);

  // ✅ Applies given styles to the selected cell (single-cell version)
  const applyStyleToSelection = (styleObj) => {
    if (!selectedCells || selectedCells.size === 0) return;

    setFiles((prevFiles) => {
      const filesCopy = [...prevFiles];

      selectedCells.forEach((cellKey) => {
        const [fileIndex, rowIndex, colIndex] = cellKey
          .split("-")
          .map((i) => parseInt(i, 10));

        const file = filesCopy[fileIndex];
        const currentCell = file?.rows?.[rowIndex]?.[colIndex];

        if (file && currentCell !== undefined) {
          const updatedCell = {
            ...getDefaultCell(currentCell),
            ...styleObj,
          };

          file.rows[rowIndex][colIndex] = updatedCell;
        }
      });

      return filesCopy;
    });

    // Save the affected file(s)
    const touchedFiles = new Set();
    selectedCells.forEach((cellKey) => {
      const [fileIndex] = cellKey.split("-").map((i) => parseInt(i, 10));
      touchedFiles.add(fileIndex);
    });
    touchedFiles.forEach((index) => saveRows(index));
  };

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const cancelRef = useRef();
  const [pendingDeleteSheetId, setPendingDeleteSheetId] = useState(null);

  return (
    <Flex>
      <CompanySidebar />
      <Box
        pl={{ base: "none", md: "10" }}
        pt={{ base: "none", md: "10" }}
        w={{ base: "100%", md: "80%" }}
      >
        <Navbar />
        <Tabs
          index={selectedTab}
          onChange={(index) => setSelectedTab(index)}
          variant={"enclosed-colored"}
          minH="750px"
          overflowY="auto"
          p={4}
          mt={10}
          borderRadius="lg"
          //border="1px solid gray"
          boxShadow="2xl"
        >
          <TabList>
            <Tab>Sheets</Tab>
            <Tab>Edit Sheet</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              {/* SECTION 1: List Existing Sheets */}
              <Box mb={6}>
                <Flex justifyContent={"space-between"}>
                  <Text fontSize="lg" fontWeight="bold" mb={4}>
                    Sheets
                  </Text>
                  <Button
                    onClick={onOpen}
                    colorScheme="blue"
                    size="sm"
                    bgGradient="linear(to-r, green.300, teal.100)"
                    _hover={{
                      bgGradient: "linear(to-r, green.100, green.400)",
                    }}
                  >
                    Add New Sheet
                  </Button>
                </Flex>

                <SimpleGrid
                  columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 6 }}
                  spacing={4}
                >
                  {sheetsFromServer.map((sheet, i) => (
                    <Box
                      key={i}
                      pl={3}
                      pr={3}
                      pt={1}
                      border="1px solid #ccc"
                      borderRadius="md"
                      _hover={{ boxShadow: "md", cursor: "pointer" }}
                      onClick={() => handleEditSheet(sheet)}
                      bgGradient="linear(to-r, green.200, teal.100, mintcream)"
                    >
                      <Flex justify="flex-end">
                        <Tooltip label="Edit">
                          <IconButton
                            icon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSheet(sheet);
                            }}
                            size="xs"
                            variant="ghost"
                            color="blue.400"
                            _hover={{ bg: "blue.50" }}
                            aria-label="Edit"
                          />
                        </Tooltip>

                        <Tooltip label="Delete">
                          <IconButton
                            icon={<DeleteIcon />}
                            size="xs"
                            variant="ghost"
                            color="red.400"
                            _hover={{ bg: "red.50" }}
                            aria-label="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDeleteSheetId(sheet._id);
                              setIsDeleteOpen(true);
                            }}
                          />
                        </Tooltip>
                      </Flex>
                      <Text
                        mb={2}
                        textAlign="center"
                        textTransform="capitalize"
                        fontWeight={"bold"}
                        fontSize={"sm"}
                      >
                        {sheet.name}
                      </Text>
                      <Flex justify="space-between">
                        <Text
                          mb={2}
                          textAlign="center"
                          color="gray.500"
                          fontSize="xs"
                        >
                          {/* {format(new Date(sheet.createdAt), "PPP")}{" "} */}
                          Edited
                          {/* like May 22, 2025 */}
                        </Text>

                        <Text
                          mb={2}
                          textAlign="center"
                          color="gray.500"
                          fontSize="xs"
                        >
                          {formatDistanceToNow(new Date(sheet.updatedAt), {
                            addSuffix: true,
                          })}
                          {/* like "5 minutes ago" */}
                        </Text>
                      </Flex>
                    </Box>
                  ))}
                </SimpleGrid>
                <AlertDialog
                  isOpen={isDeleteOpen}
                  leastDestructiveRef={cancelRef}
                  onClose={() => setIsDeleteOpen(false)}
                >
                  <AlertDialogOverlay>
                    <AlertDialogContent>
                      <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        Delete Sheet
                      </AlertDialogHeader>

                      <AlertDialogBody>
                        Are you sure? This action cannot be undone.
                      </AlertDialogBody>

                      <AlertDialogFooter>
                        <Button
                          ref={cancelRef}
                          onClick={() => setIsDeleteOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          colorScheme="red"
                          onClick={() => {
                            handleDeleteSheet(pendingDeleteSheetId);
                            setIsDeleteOpen(false);
                            setPendingDeleteSheetId(null);
                          }}
                          ml={3}
                        >
                          Delete
                        </Button>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialogOverlay>
                </AlertDialog>
              </Box>

              {/* Create Sheet Modal */}
              <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Create New Sheet</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Input
                      placeholder="Enter sheet name"
                      value={newSheetName}
                      onChange={(e) => setNewSheetName(e.target.value)}
                      mb={3}
                    />
                    <Input
                      type="file"
                      accept=".xlsx"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                    />
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      colorScheme="blue"
                      mr={3}
                      onClick={handleCreateSheet}
                    >
                      Create
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </TabPanel>
            {/* TAB 1: Edit Sheet */}
            <TabPanel>
              {files?.length > 0 && files[editingSheetIndex] && (
                <Box p={4}>
                  <Flex justifyContent={"space-between"}>
                    <Text fontWeight="bold" mb={2}>
                      Editing: {files[editingSheetIndex].name}
                    </Text>
                    <Box>
                      <Button
                        onClick={saveEditedSheet}
                        colorScheme="green"
                        size="sm"
                        bgGradient="linear(to-r, green.300, teal.100)"
                      >
                        Save
                      </Button>
                      <Button
                        onClick={exportExcel}
                        colorScheme="green"
                        size="sm"
                        bgGradient="linear(to-r, green.300, teal.100)"
                        ml={4}
                      >
                        Export
                      </Button>
                    </Box>
                  </Flex>
                  <Box p={2} bg="gray.100" rounded="md" mb={4}>
                    <HStack
                      spacing={3}
                      p={2}
                      borderBottom="1px solid gray"
                      bg="gray.50"
                    >
                      <Button
                        size="sm"
                        variant={
                          activeStyles.fontWeight === "bold"
                            ? "solid"
                            : "outline"
                        }
                        fontWeight="bold"
                        onClick={() =>
                          applyStyleToSelection({
                            fontWeight:
                              activeStyles.fontWeight === "bold"
                                ? "normal"
                                : "bold",
                          })
                        }
                      >
                        B
                      </Button>

                      {/* Italic Toggle */}
                      <Button
                        size="sm"
                        variant={
                          activeStyles.fontStyle === "italic"
                            ? "solid"
                            : "outline"
                        }
                        fontStyle="italic"
                        onClick={() =>
                          applyStyleToSelection({
                            fontStyle:
                              activeStyles.fontStyle === "italic"
                                ? "normal"
                                : "italic",
                          })
                        }
                      >
                        I
                      </Button>

                      <Button
                        size="sm"
                        variant={
                          activeStyles.textDecoration === "underline"
                            ? "solid"
                            : "outline"
                        }
                        textDecoration="underline"
                        onClick={() =>
                          applyStyleToSelection({
                            textDecoration:
                              activeStyles.textDecoration === "underline"
                                ? "none"
                                : "underline",
                          })
                        }
                      >
                        U
                      </Button>

                      <Input
                        type="color"
                        size="sm"
                        onChange={(e) =>
                          applyStyleToSelection({ color: e.target.value })
                        }
                      />
                      <Input
                        type="color"
                        size="sm"
                        onChange={(e) =>
                          applyStyleToSelection({
                            backgroundColor: e.target.value,
                          })
                        }
                      />
                    </HStack>
                  </Box>
                  <VStack
                    spacing={4}
                    align="start"
                    p={4}
                    m={4}
                    border="1px"
                    borderColor="gray.300"
                    rounded="md"
                  >
                    {files[editingSheetIndex].rows?.map((row, rowIndex) => {
                      const safeRow = Array.isArray(row) ? row : [];
                      return (
                        <HStack key={rowIndex} align="center">
                          {Array.isArray(row) &&
                            row?.map((cell, colIndex) => {
                              const currentCell =
                                typeof cell === "string"
                                  ? { value: cell }
                                  : cell;
                              return (
                                // <Input
                                //   key={colIndex}
                                //   value={currentCell.value || ""}
                                //   onChange={(e) =>
                                //     handleCellChange(
                                //       editingSheetIndex,
                                //       rowIndex,
                                //       colIndex,
                                //       {
                                //         ...currentCell,
                                //         value: e.target.value,
                                //       }
                                //     )
                                //   }
                                //   size="sm"
                                //   style={{
                                //     fontWeight: currentCell.fontWeight,
                                //     fontStyle: currentCell.fontStyle,
                                //     textDecoration: currentCell.textDecoration,
                                //     color: currentCell.color,
                                //     backgroundColor:
                                //       currentCell.backgroundColor,
                                //   }}
                                // />
                                <Input
                                  key={colIndex}
                                  value={currentCell.value || ""}
                                  onClick={() => {
                                    const key = `${editingSheetIndex}-${rowIndex}-${colIndex}`;
                                    setSelectedCells(new Set([key]));
                                  }}
                                  onChange={(e) =>
                                    handleCellChange(
                                      editingSheetIndex,
                                      rowIndex,
                                      colIndex,
                                      {
                                        ...currentCell,
                                        value: e.target.value,
                                      }
                                    )
                                  }
                                  size="sm"
                                  style={{
                                    fontWeight:
                                      currentCell.fontWeight || "normal",
                                    fontStyle:
                                      currentCell.fontStyle || "normal",
                                    textDecoration:
                                      currentCell.textDecoration || "none",
                                    color: currentCell.color || "#000000",
                                    backgroundColor:
                                      currentCell.backgroundColor || "#ffffff",
                                  }}
                                />
                              );
                            })}
                          <IconButton
                            size="xs"
                            icon={<RiDeleteBin2Line />}
                            onClick={() =>
                              removeRow(editingSheetIndex, rowIndex)
                            }
                            aria-label="Delete Row"
                          />
                        </HStack>
                      );
                    })}

                    <HStack mt={4}>
                      <Button
                        onClick={() => addRow(editingSheetIndex)}
                        size="sm"
                      >
                        Add Row
                      </Button>
                      <Button
                        onClick={() => addColumn(editingSheetIndex)}
                        size="sm"
                      >
                        Add Column
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Flex>
  );
};

export default CompanySheets;
